function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from "react";
import PropTypes from "prop-types";
import { blue15, grey3, grey20, info40, spacing32, white } from "@churchofjesuschrist/eden-style-constants";
import hexToRgba from "hex2rgba";
import styled from "styled-components";
import shared from "./shared";
const iconSpecificFontSize = "1.5rem";
const Component = styled.button.withConfig({
  componentId: "sc-1g7hsbc-0"
})(["", ";border:none;position:relative;&:before{content:\"\";display:block;width:100%;height:100%;position:absolute;}border-radius:50%;", ";font-size:", ";padding:0;background:transparent;color:", ";@media (hover:hover){&:hover{background:", ";color:", ";}}&:active{background:", ";}&[aria-pressed=\"true\"]{color:", ";}&[disabled],&[disabled]:hover{background:transparent;color:", ";}"], shared, _ref => {
  let {
    size
  } = _ref;
  return size === "large" ? `min-width: 3.5rem;
        min-height: 3.5rem;` : size === "small" ? `min-width: ${spacing32};
          min-height: ${spacing32};` : `min-width: 2.5rem;
    min-height: 2.5rem;`;
}, iconSpecificFontSize, _ref2 => {
  let {
    inverted
  } = _ref2;
  return inverted ? white : "currentColor";
}, _ref3 => {
  let {
    inverted
  } = _ref3;
  return inverted ? hexToRgba(white, 0.16) : hexToRgba(grey3, 0.6);
}, _ref4 => {
  let {
    inverted
  } = _ref4;
  return inverted ? white : "currentColor";
}, _ref5 => {
  let {
    inverted
  } = _ref5;
  return inverted ? hexToRgba(white, 0.24) : grey3;
}, _ref6 => {
  let {
    inverted
  } = _ref6;
  return inverted ? blue15 : info40;
}, _ref7 => {
  let {
    inverted
  } = _ref7;
  return inverted ? hexToRgba(white, 0.38) : grey20;
});
const Icon = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(Component, _extends({}, props, {
  ref: ref,
  as: props.disabled ? "button" : props.href ? "a" : "button"
}), props.children));
Icon.defaultProps = {
  type: "button",
  size: "normal"
};
Icon.displayName = "Icon";
Icon.propTypes = {
  /** The icon */
  children: PropTypes.node.isRequired,

  /** When present, the button becomes a "toggle" button. The value indicates whether it is toggled on or not. */
  "aria-pressed": PropTypes.oneOf([true, false]),

  /** Defines a value that labels the button. If another element is visible in your UI that already labels the
   * purpose of the button, use the 'aria-labelledby' prop instead. This prop is required if `aria-labelledby` is not used. */
  "aria-label": PropTypes.string,

  /** Identifies the element(s) that label(s) the button. Must be a space-separated list of one or more `id` values referencing
   * the element(s) present in the UI that label(s) the button. If no accompanying visible label(s) exist, you must instead use
   * the 'aria-label' prop instead. */
  "aria-labelledby": PropTypes.string,

  /** Whether to apply styles that help the button stand out on a dark background */
  inverted: PropTypes.bool,

  /** The size of the button */
  size: PropTypes.oneOf(["small", "normal", "large"]),

  /** The [type of the button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Attributes), if an href is present it will be the [type of the link](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Attributes) */
  type: PropTypes.string,

  /** If the button should be disabled */
  disabled: PropTypes.bool,

  /** The link that the button should navigate to on click */
  href: PropTypes.string
};
export default Icon;